import _, { isEqual, isNil } from 'lodash'
import React from 'react'
import {
  CacheableRolodexEntryJson,
  createGeckoAssetModel,
  DataFrameSettings,
  DuckDbFeatureFlag,
  GeckoActionLog,
  GeckoAssetModel,
  GeckoAssetModelType,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoComponentDataset,
  GeckoComponentDefaults,
  GeckoEntry,
  GeckoGeographicPin,
  GeckoInspection,
  GeckoJob,
  GeckoKestrelModel,
  GeckoLegacyBoilerAssetModel,
  GeckoMachinedForgingAssetModel,
  GeckoMarkupLayer,
  GeckoMillPlan,
  GeckoObservationTicket,
  GeckoOrganization,
  GeckoPhotogrammetryModel,
  GeckoPipelineAssetModel,
  GeckoPointCloudScene,
  GeckoPotreePointCloud,
  GeckoReport,
  GeckoSite,
  GeckoSiteConfig,
  GeckoSiteModel,
  GeckoSiteObservation,
  GeckoSource,
  GeckoUnit,
  GeckoUserComment,
  GeckoVerticalTankAssetModel,
  GeckoView,
  GeckoVisualInspection,
  GeckoVisualInspectionField,
  GeckoVisualInspectionFieldResponse,
  GeckoVisualInspectionForm,
  GeckoVisualInspectionFormResponse,
  GeckoVisualInspectionSection,
  GeckoVisualInspectionSectionResponse,
  GeckoWalkdown,
  RolodexEntryJson,
  RolodexEntryPatchJson,
  RolodexEntryType,
  RolodexLoadedEntryJson,
  ThirdPartyInspectionDetails,
} from 'types'

const DUCKDB_FEATURE_FLAG_TYPE = 'forecast-duckdb-feature-flag'

// ----------------------------------------------------------------------------
// Types

export type EntryLookupFunction<T extends GeckoEntry> = (
  key: string | undefined
) => T | undefined

export type RolodexEntryDispenserType = {
  //----------------------------------------------------------------------------

  /** A readonly array of all entries loaded in this query */
  entries: ReadonlyArray<GeckoEntry>
  /** A readonly array of all organizations loaded in this query */
  organizations: ReadonlyArray<GeckoOrganization>
  /** A readonly array of all sites loaded in this query */
  sites: ReadonlyArray<GeckoSite>
  /** A readonly array of all site configs loaded in this query */
  siteConfigs: ReadonlyArray<GeckoSiteConfig>
  /** A readonly array of all units loaded in this query */
  units: ReadonlyArray<GeckoUnit>
  /** A readonly array of all formatters loaded in this query */
  components: ReadonlyArray<GeckoComponent>
  /** A readonly array of all jobs loaded in this query */
  jobs: ReadonlyArray<GeckoJob>
  /** A readonly array of all inspections loaded in this query */
  inspections: ReadonlyArray<GeckoInspection>
  /** A readonly array of all visual inspections loaded in this query */
  visualInspections: ReadonlyArray<GeckoVisualInspection>
  /** A readonly array of all visual inspection forms loaded in this query */
  visualInspectionForms: ReadonlyArray<GeckoVisualInspectionForm>
  /** A readonly array of all visual inspection sections loaded in this query */
  visualInspectionSections: ReadonlyArray<GeckoVisualInspectionSection>
  /** A readonly array of all visual inspection fields loaded in this query */
  visualInspectionFields: ReadonlyArray<GeckoVisualInspectionField>
  /** A readonly array of all visual inspection form responses loaded in this query */
  visualInspectionFormResponses: ReadonlyArray<GeckoVisualInspectionFormResponse>
  /** A readonly array of all visual inspection section responses loaded in this query */
  visualInspectionSectionResponses: ReadonlyArray<GeckoVisualInspectionSectionResponse>
  /** A readonly array of all visual inspection field responses loaded in this query */
  visualInspectionFieldResponses: ReadonlyArray<GeckoVisualInspectionFieldResponse>
  /** A readonly array of all reports loaded in this query */
  reports: ReadonlyArray<GeckoReport>
  /** A readonly array of all component data frames (CDFs) loaded in this query */
  componentDataFrames: ReadonlyArray<GeckoComponentDataFrame>
  /** A readonly array of all component data sets (CDSs) loaded in this query */
  componentDatasets: ReadonlyArray<GeckoComponentDataset>
  /** A readonly array of all action logs loaded in this query */
  actionLogs: ReadonlyArray<GeckoActionLog>
  /** A readonly array of all markup layers loaded in this query */
  markupLayers: ReadonlyArray<GeckoMarkupLayer>
  /** A readonly array of all the asset models loaded in this query */
  assetModels: ReadonlyArray<GeckoAssetModel>
  /** A readonly array of all component defaults objects in this query */
  componentDefaults: ReadonlyArray<GeckoComponentDefaults>
  /** A readonly array of all the photogrammetry models loaded in this query */
  photogrammetryModels: ReadonlyArray<GeckoPhotogrammetryModel>
  /** A readonly array of all the kestrel models loaded in this query */
  kestrelModels: ReadonlyArray<GeckoKestrelModel>
  /** A readonly array of all the site models loaded in this query */
  siteModels: ReadonlyArray<GeckoSiteModel>
  /** A readonly array of all site observations loaded in this query */
  siteObservations: ReadonlyArray<GeckoSiteObservation>
  /** A readonly array of all observation tickets loaded in this query */
  observationTickets: ReadonlyArray<GeckoObservationTicket>
  duckDbFeatureFlag: DuckDbFeatureFlag
  thirdPartyInspectionDetails: ReadonlyArray<ThirdPartyInspectionDetails>
  potreePointClouds: ReadonlyArray<GeckoPotreePointCloud>
  pointCloudScenes: ReadonlyArray<GeckoPointCloudScene>
  geographicPins: ReadonlyArray<GeckoGeographicPin>
  /** A readonly array of all user comments loaded in this query */
  userComments: ReadonlyArray<GeckoUserComment>
  millPlans: ReadonlyArray<GeckoMillPlan>
  /** A readonly array of all walkdowns loaded in this query */
  walkdowns: ReadonlyArray<GeckoWalkdown>

  //----------------------------------------------------------------------------

  /** A helper method for grabbing an entry from the query results by ID */
  getEntry: EntryLookupFunction<GeckoEntry>
  /** A helper method for grabbing an organization from the query results by ID */
  getOrganization: EntryLookupFunction<GeckoOrganization>
  /** A helper method for grabbing a site from the query results by ID */
  getSite: EntryLookupFunction<GeckoSite>
  /** A helper method for grabbing a site config from the query results by ID */
  getSiteConfig: EntryLookupFunction<GeckoSiteConfig>
  /** A helper method for grabbing a unit from the query results by ID */
  getUnit: EntryLookupFunction<GeckoUnit>
  /** A helper method for grabbing a component from the query results by ID */
  getComponent: EntryLookupFunction<GeckoComponent>
  /** A helper method for grabbing a job from the query results by ID */
  getJob: EntryLookupFunction<GeckoJob>
  /** A helper method for grabbing an inspection from the query results by ID */
  getInspection: EntryLookupFunction<GeckoInspection>
  /** A helper method for grabbing a visual inspection from the query results by ID */
  getVisualInspection: EntryLookupFunction<GeckoVisualInspection>
  /** A helper method for grabbing a visual inspection form from the query results by ID */
  getVisualInspectionForm: EntryLookupFunction<GeckoVisualInspectionForm>
  /** A helper method for grabbing a visual inspection section from the query results by ID */
  getVisualInspectionSection: EntryLookupFunction<GeckoVisualInspectionSection>
  /** A helper method for grabbing a visual inspection field from the query results by ID */
  getVisualInspectionField: EntryLookupFunction<GeckoVisualInspectionField>
  /** A helper method for grabbing a visual inspection form response from the query results by ID */
  getVisualInspectionFormResponse: EntryLookupFunction<GeckoVisualInspectionFormResponse>
  /** A helper method for grabbing a visual inspection section response from the query results by ID */
  getVisualInspectionSectionResponse: EntryLookupFunction<GeckoVisualInspectionSectionResponse>
  /** A helper method for grabbing a visual inspection field response from the query results by ID */
  getVisualInspectionFieldResponse: EntryLookupFunction<GeckoVisualInspectionFieldResponse>
  /** A helper method for grabbing a report from the query results by ID */
  getReport: EntryLookupFunction<GeckoReport>
  /** A helper method for grabbing a component data frame (CDF) from the query results by ID */
  getComponentDataFrame: EntryLookupFunction<GeckoComponentDataFrame>
  /** A helper method for grabbing a component data set (CDS) from the query results by ID */
  getComponentDataSet: EntryLookupFunction<GeckoComponentDataset>
  /** A helper method for grabbing an action log from the query results by ID */
  getActionLog: EntryLookupFunction<GeckoActionLog>
  /** A helper method for grabbing a markup layer from the query results by ID */
  getMarkupLayer: EntryLookupFunction<GeckoMarkupLayer>
  /** A helper method for grabbing an asset model from the query results by ID */
  getAssetModel: EntryLookupFunction<GeckoAssetModel>
  /** A helper method for grabbing a photogrammetry model from the query results by ID */
  getPhotogrammetryModel: EntryLookupFunction<GeckoPhotogrammetryModel>
  /** A helper method for grabbing a kestrel model from the query results by ID */
  getKestrelModel: EntryLookupFunction<GeckoKestrelModel>
  /** A helper method for grabbing a site observation from the query results by ID */
  getSiteObservation: EntryLookupFunction<GeckoSiteObservation>
  /** A helper method for grabbing an observation ticket from the query results by ID */
  getObservationTicket: EntryLookupFunction<GeckoObservationTicket>
  /** A helper method for grabbing a site model from the query results by ID */
  getSiteModel: EntryLookupFunction<GeckoSiteModel>
  /** A helper method for grabbing a geographic pin from the query results by ID */
  getGeographicPin: EntryLookupFunction<GeckoGeographicPin>
  /** A helper method for grabbing a user comment from the query results by ID */
  getUserComment: EntryLookupFunction<GeckoUserComment>
  /** A helper method for grabbing a mill plan from the query results by ID */
  getMillPlan: EntryLookupFunction<GeckoMillPlan>
  /** A helper method for grabbing a walkdown from the query results by ID */
  getWalkdown: EntryLookupFunction<GeckoWalkdown>
}

// ----------------------------------------------------------------------------
// Main Hook

/**
 * A hook for dispensing entries from Rolodex as GeckoEntry objects
 */
export function useDispenseRolodexProperAndCacheEntries(
  rolodexProperFetchData: RolodexLoadedEntryJson[][] | undefined,
  rolodexCacheFetchData:
    | ReadonlyMap<string, RolodexEntryPatchJson | 'deleted'>
    | undefined
): RolodexEntryDispenserType {
  const duckDbFeatureFlag = React.useMemo(
    () => determineDuckDbFeatureFlag(rolodexProperFetchData),
    [rolodexProperFetchData]
  )

  //use query param feature flag, pass through to createDispenserFromGeckoEntryArray()
  const rolodexProperEntriesAsForecastClass = React.useMemo((): {
    id: string
    classObj: GeckoEntry
    index: number
    json: RolodexEntryJson
  }[] => {
    const newSoftDeletedEntryIDs = new Set<string>()

    // TODO ENT-1028: is this (index) really the best way to preserve sorting order?
    const allEntriesAsClassObjs: {
      id: string
      classObj: GeckoEntry
      index: number
      json: RolodexEntryJson
    }[] = []
    rolodexProperFetchData?.forEach((arr) =>
      arr.forEach(({ entry, links }) => {
        if (
          (entry.type === RolodexEntryType.COMPONENT_DATA_FRAME ||
            entry.type === RolodexEntryType.FDE_COMPONENT_DATA_FRAME) &&
          duckDbFeatureFlag.isEntryEnabled(entry)
        ) {
          return
        }
        if (
          entry.type === RolodexEntryType.COMPONENT_DATASET &&
          duckDbFeatureFlag.isEntryEnabled(entry) &&
          (entry as GeckoComponentDataset).data.schema_version < 6
        ) {
          return
        }
        const entryAsClassObj = createClassObjForEntryJson(entry)
        if (isNil(entryAsClassObj)) {
          return
        }

        allEntriesAsClassObjs.push({
          id: entry.id,
          classObj: entryAsClassObj,
          index: allEntriesAsClassObjs.length,
          json: entry,
        })

        // if this is a CDS and we are fetching from parquet, create CDFs from it
        if (
          entryAsClassObj instanceof GeckoComponentDataset &&
          GeckoComponentDataset.AreTagsValid(entryAsClassObj) &&
          duckDbFeatureFlag.isEntryEnabled(entry)
        ) {
          const cdfClassObjects =
            createComponentDataFramesFromComponentDataset(entryAsClassObj)

          cdfClassObjects.forEach((cdf) => {
            allEntriesAsClassObjs.push({
              id: cdf.id,
              classObj: cdf,
              index: allEntriesAsClassObjs.length,
              json: cdf.raw,
            })
          })
        }

        // TODO ENT-1027: bump the generation here somehow
        if (links) {
          Object.values(links).forEach((linkedEntry) => {
            // TODO ENT-1028: Totally wrong, as far as sorting order is concerned!
            const linkedEntryAsClassObj =
              createClassObjForEntryJson(linkedEntry)
            if (isNil(linkedEntryAsClassObj)) {
              return
            }
            allEntriesAsClassObjs.push({
              id: linkedEntry.id,
              classObj: linkedEntryAsClassObj,
              index: allEntriesAsClassObjs.length,
              json: linkedEntry,
            })
          })
        }
      })
    )

    if (newSoftDeletedEntryIDs.size > 0)
      console.warn(
        `Soft deleted entries:`,
        Array.from(newSoftDeletedEntryIDs.values())
      )

    allEntriesAsClassObjs.push({
      id: duckDbFeatureFlag.id,
      classObj: duckDbFeatureFlag,
      index: allEntriesAsClassObjs.length,
      json: duckDbFeatureFlag.raw,
    })
    return allEntriesAsClassObjs
  }, [rolodexProperFetchData, duckDbFeatureFlag])

  const rolodexCacheEntriesAsForecastClass = React.useMemo((): {
    id: string
    classObj: GeckoEntry | 'deleted'
    index: number
    json: RolodexEntryJson | 'deleted'
  }[] => {
    const allEntriesAsClassObjs: {
      id: string
      classObj: GeckoEntry | 'deleted'
      index: number
      json: RolodexEntryJson | 'deleted'
    }[] = []
    if (rolodexCacheFetchData) {
      rolodexCacheFetchData.forEach((entry, id) => {
        if (entry === 'deleted') {
          allEntriesAsClassObjs.push({
            id,
            classObj: 'deleted',
            index: allEntriesAsClassObjs.length,
            json: 'deleted',
          })
          return
        }
        const patchedJSON = {
          id: id,
          isFromCache: true,
        }
        _.merge(patchedJSON, entry)
        const linkedEntryAsClassObj = createClassObjForEntryJson(
          patchedJSON as CacheableRolodexEntryJson
        )
        if (linkedEntryAsClassObj) {
          allEntriesAsClassObjs.push({
            id,
            classObj: linkedEntryAsClassObj,
            index: allEntriesAsClassObjs.length,
            json: patchedJSON as CacheableRolodexEntryJson,
          })
        }
      })
    }
    return allEntriesAsClassObjs
  }, [rolodexCacheFetchData])

  // ----------------------------------------------------------------------------
  // Merge entry lists

  return React.useMemo<RolodexEntryDispenserType>(() => {
    const map = new Map<
      string,
      { classObj: GeckoEntry; index: number; json: RolodexEntryJson }
    >()
    rolodexProperEntriesAsForecastClass.forEach((entryProperties) => {
      map.set(entryProperties.id, entryProperties)
    })

    rolodexCacheEntriesAsForecastClass.forEach(
      ({ classObj, json, id, index }) => {
        if (json === 'deleted' || classObj === 'deleted') {
          map.delete(id)
          return
        }
        const rolodexEntryWithId = map.get(id)
        // If there is an entry in Rolodex-Proper with the same ID, apply the cache entry to it using lodash.merge if possible
        if (rolodexEntryWithId) {
          const mergedEntry = {}
          _.merge(mergedEntry, rolodexEntryWithId.json, json)
          if (isEqual(mergedEntry, json)) {
            // if the entries are the same, keep the original classObj as to not create a new reference
            map.set(id, { classObj, index, json })
            return
          }

          const newClassObj = createClassObjForEntryJson(
            mergedEntry as CacheableRolodexEntryJson
          )
          if (isNil(newClassObj)) {
            // this was soft-deleted in proper, but a cache entry exists
            map.set(id, { classObj, json, index })
            return
          }

          map.set(id, {
            classObj: newClassObj,
            index,
            json: mergedEntry as CacheableRolodexEntryJson,
          })
          return
        }

        // If there is no entry in Rolodex-Proper with the same ID, add the cache entry as a new entry
        map.set(id, { classObj, index, json })
        return
      }
    )

    const classObjArray = Array.from(map.values())
      .sort(({ index: indexA }, { index: indexB }) => indexA - indexB)
      .map(({ classObj }) => classObj)

    return createDispenserFromGeckoEntryArray(classObjArray)
  }, [rolodexCacheEntriesAsForecastClass, rolodexProperEntriesAsForecastClass])
}

// ----------------------------------------------------------------------------
// Helper Functions

function createClassObjForEntryJson(
  json: CacheableRolodexEntryJson
): GeckoEntry | undefined {
  try {
    switch (json.type) {
      case 'organization':
        return GeckoOrganization.CreateFromJSON(json)
      case 'site':
        return GeckoSite.CreateFromJSON(json)
      case 'site-config':
        return GeckoSiteConfig.CreateFromJSON(json)
      case 'unit':
        return GeckoUnit.CreateFromJSON(json)
      case 'component':
        return GeckoComponent.CreateFromJSON(json)
      case 'job':
        return GeckoJob.CreateFromJSON(json)
      case 'inspection':
        return GeckoInspection.CreateFromJSON(json)
      case RolodexEntryType.VISUAL_INSPECTION:
        return GeckoVisualInspection.CreateFromJSON(json)
      case RolodexEntryType.VISUAL_INSPECTION_FORM:
        return GeckoVisualInspectionForm.CreateFromJSON(json)
      case RolodexEntryType.VISUAL_INSPECTION_SECTION:
        return GeckoVisualInspectionSection.CreateFromJSON(json)
      case RolodexEntryType.VISUAL_INSPECTION_FIELD:
        return GeckoVisualInspectionField.CreateFromJSON(json)
      case RolodexEntryType.VISUAL_INSPECTION_FORM_RESPONSE:
        return GeckoVisualInspectionFormResponse.CreateFromJSON(json)
      case RolodexEntryType.VISUAL_INSPECTION_SECTION_RESPONSE:
        return GeckoVisualInspectionSectionResponse.CreateFromJSON(json)
      case RolodexEntryType.VISUAL_INSPECTION_FIELD_RESPONSE:
        return GeckoVisualInspectionFieldResponse.CreateFromJSON(json)
      case 'report':
        return GeckoReport.CreateFromJSON(json)
      case 'component-dataset':
        return GeckoComponentDataset.CreateFromJSON(json)
      case 'action-log':
        return GeckoActionLog.CreateFromJSON(json)
      case 'component-data-frame':
      case 'fde-component-data-frame':
        return GeckoComponentDataFrame.CreateFromJSON(json)
      case 'markup-layer':
        return GeckoMarkupLayer.CreateFromJSON(json)
      case 'asset-model':
        return createGeckoAssetModel(json)
      case 'component-defaults':
        return GeckoComponentDefaults.CreateFromJSON(json)
      case 'photogrammetry-model':
        return GeckoPhotogrammetryModel.CreateFromJSON(json)
      case 'kestrel-model':
        return GeckoKestrelModel.CreateFromJSON(json)
      case 'forecast-duckdb-feature-flag':
        return DuckDbFeatureFlag.CreateFromJSON(json)
      case 'third-party-inspection-details':
        return ThirdPartyInspectionDetails.CreateFromJSON(json)
      case RolodexEntryType.POTREE_POINT_CLOUD:
        return GeckoPotreePointCloud.CreateFromJSON(json)
      case RolodexEntryType.POINT_CLOUD_SCENE:
        return GeckoPointCloudScene.CreateFromJSON(json)
      case RolodexEntryType.OBSERVATION:
        return GeckoSiteObservation.CreateFromJSON(json)
      case RolodexEntryType.OBSERVATION_TICKET:
        return GeckoObservationTicket.CreateFromJSON(json)
      case 'site-model':
        return GeckoSiteModel.CreateFromJSON(json)
      case RolodexEntryType.GEOGRAPHIC_PIN:
        return GeckoGeographicPin.CreateFromJSON(json)
      case RolodexEntryType.USER_COMMENT:
        return GeckoUserComment.CreateFromJSON(json)
      case RolodexEntryType.WALKDOWN:
        return GeckoWalkdown.CreateFromJSON(json)
      default:
        return {
          id: json.id,
          type: json.type,
          data: { ...json.data },
          tags: { ...json.tags },
          index: { ...json.index },
          raw: json,
          generation: json.generation,
          updated_at: json.updated_at,
          isFromCache: json.isFromCache ?? false,
          view_restriction: null,
        }
    }
  } catch (error) {
    console.warn(`Failed to create object for "${json.id}"`, error)
    return undefined
  }
}

function createDispenserFromGeckoEntryArray(
  entryClassObjs: GeckoEntry[]
): RolodexEntryDispenserType {
  const allEntriesMap = new Map<string | undefined, GeckoEntry>()
  const organizationMap = new Map<string | undefined, GeckoOrganization>()
  const siteMap = new Map<string | undefined, GeckoSite>()
  const siteConfigMap = new Map<string | undefined, GeckoSiteConfig>()
  const unitMap = new Map<string | undefined, GeckoUnit>()
  const componentMap = new Map<string | undefined, GeckoComponent>()
  const jobMap = new Map<string | undefined, GeckoJob>()
  const inspectionMap = new Map<string | undefined, GeckoInspection>()
  const visualInspectionMap = new Map<
    string | undefined,
    GeckoVisualInspection
  >()
  const visualInspectionFormMap = new Map<
    string | undefined,
    GeckoVisualInspectionForm
  >()
  const visualInspectionSectionMap = new Map<
    string | undefined,
    GeckoVisualInspectionSection
  >()
  const visualInspectionFieldMap = new Map<
    string | undefined,
    GeckoVisualInspectionField
  >()
  const visualInspectionFormResponseMap = new Map<
    string | undefined,
    GeckoVisualInspectionFormResponse
  >()
  const visualInspectionSectionResponseMap = new Map<
    string | undefined,
    GeckoVisualInspectionSectionResponse
  >()
  const visualInspectionFieldResponseMap = new Map<
    string | undefined,
    GeckoVisualInspectionFieldResponse
  >()
  const reportMap = new Map<string | undefined, GeckoReport>()
  const componentDataFrameMap = new Map<
    string | undefined,
    GeckoComponentDataFrame
  >()
  const componentDataSetMap = new Map<
    string | undefined,
    GeckoComponentDataset
  >()
  const actionLogMap = new Map<string | undefined, GeckoActionLog>()
  const componentDefaultsMap = new Map<
    string | undefined,
    GeckoComponentDefaults
  >()
  const markupLayerMap = new Map<string | undefined, GeckoMarkupLayer>()
  const assetModelMap = new Map<string | undefined, GeckoAssetModel>()
  const photogrammetryModelMap = new Map<
    string | undefined,
    GeckoPhotogrammetryModel
  >()
  const kestrelModelMap = new Map<string | undefined, GeckoKestrelModel>()
  const thirdPartyInspectionDetailsMap = new Map<
    string | undefined,
    ThirdPartyInspectionDetails
  >()
  const potreePointCloudMap = new Map<
    string | undefined,
    GeckoPotreePointCloud
  >()
  const pointCloudSceneMap = new Map<string | undefined, GeckoPointCloudScene>()
  const siteObservationMap = new Map<string | undefined, GeckoSiteObservation>()
  const observationTicketMap = new Map<
    string | undefined,
    GeckoObservationTicket
  >()
  const siteModelMap = new Map<string | undefined, GeckoSiteModel>()
  const geographicPinMap = new Map<string | undefined, GeckoGeographicPin>()
  const userCommentMap = new Map<string | undefined, GeckoUserComment>()
  const millPlanMap = new Map<string | undefined, GeckoMillPlan>()
  const walkdownMap = new Map<string | undefined, GeckoWalkdown>()

  // Sort into maps
  entryClassObjs.forEach((entry) => {
    switch (entry.type) {
      case 'organization':
        organizationMap.set(entry.id, entry as GeckoOrganization)
        break
      case 'site':
        siteMap.set(entry.id, entry as GeckoSite)
        break
      case 'site-config':
        siteConfigMap.set(entry.id, entry as GeckoSiteConfig)
        break
      case 'unit':
        unitMap.set(entry.id, entry as GeckoUnit)
        break
      case 'component':
        componentMap.set(entry.id, entry as GeckoComponent)
        break
      case 'job':
        jobMap.set(entry.id, entry as GeckoJob)
        break
      case 'inspection':
        inspectionMap.set(entry.id, entry as GeckoInspection)
        break
      case RolodexEntryType.VISUAL_INSPECTION:
        visualInspectionMap.set(entry.id, entry as GeckoVisualInspection)
        break
      case RolodexEntryType.VISUAL_INSPECTION_FORM:
        visualInspectionFormMap.set(
          entry.id,
          entry as GeckoVisualInspectionForm
        )
        break
      case RolodexEntryType.VISUAL_INSPECTION_SECTION:
        visualInspectionSectionMap.set(
          entry.id,
          entry as GeckoVisualInspectionSection
        )
        break
      case RolodexEntryType.VISUAL_INSPECTION_FIELD:
        visualInspectionFieldMap.set(
          entry.id,
          entry as GeckoVisualInspectionField
        )
        break
      case RolodexEntryType.VISUAL_INSPECTION_FORM_RESPONSE:
        visualInspectionFormResponseMap.set(
          entry.id,
          entry as GeckoVisualInspectionFormResponse
        )
        break
      case RolodexEntryType.VISUAL_INSPECTION_SECTION_RESPONSE:
        visualInspectionSectionResponseMap.set(
          entry.id,
          entry as GeckoVisualInspectionSectionResponse
        )
        break
      case RolodexEntryType.VISUAL_INSPECTION_FIELD_RESPONSE:
        visualInspectionFieldResponseMap.set(
          entry.id,
          entry as GeckoVisualInspectionFieldResponse
        )
        break
      case 'report':
        reportMap.set(entry.id, entry as GeckoReport)
        break
      case 'component-dataset':
        componentDataSetMap.set(entry.id, entry as GeckoComponentDataset)
        break
      case 'action-log':
        actionLogMap.set(entry.id, entry as GeckoActionLog)
        break
      case 'component-data-frame':
      case 'fde-component-data-frame':
        componentDataFrameMap.set(entry.id, entry as GeckoComponentDataFrame)
        break
      case 'markup-layer':
        markupLayerMap.set(entry.id, entry as GeckoMarkupLayer)
        break
      case 'asset-model':
        assetModelMap.set(entry.id, getAssetModelType(entry))
        break
      case 'component-defaults':
        componentDefaultsMap.set(entry.id, entry as GeckoComponentDefaults)
        break
      case 'photogrammetry-model':
        photogrammetryModelMap.set(entry.id, entry as GeckoPhotogrammetryModel)
        break
      case 'kestrel-model':
        kestrelModelMap.set(entry.id, entry as GeckoKestrelModel)
        break
      case 'third-party-inspection-details':
        thirdPartyInspectionDetailsMap.set(
          entry.id,
          entry as ThirdPartyInspectionDetails
        )
        break
      case RolodexEntryType.POTREE_POINT_CLOUD:
        potreePointCloudMap.set(entry.id, entry as GeckoPotreePointCloud)
        break
      case RolodexEntryType.POINT_CLOUD_SCENE:
        pointCloudSceneMap.set(entry.id, entry as GeckoPointCloudScene)
        break
      case RolodexEntryType.OBSERVATION:
        siteObservationMap.set(entry.id, entry as GeckoSiteObservation)
        break
      case RolodexEntryType.OBSERVATION_TICKET:
        observationTicketMap.set(entry.id, entry as GeckoObservationTicket)
        break
      case 'site-model':
        siteModelMap.set(entry.id, entry as GeckoSiteModel)
        break
      case RolodexEntryType.GEOGRAPHIC_PIN:
        geographicPinMap.set(entry.id, entry as GeckoGeographicPin)
        break
      case RolodexEntryType.USER_COMMENT:
        userCommentMap.set(entry.id, entry as GeckoUserComment)
        break
      case RolodexEntryType.MILL_PLAN:
        millPlanMap.set(entry.id, entry as GeckoMillPlan)
        break
      case RolodexEntryType.WALKDOWN:
        walkdownMap.set(entry.id, entry as GeckoWalkdown)
        break
      default:
        break
    }
    allEntriesMap.set(entry.id, entry)
  })

  const entries = Array.from(allEntriesMap.values())
  const organizations = Array.from(organizationMap.values())
  const sites = Array.from(siteMap.values())
  const siteConfigs = Array.from(siteConfigMap.values())
  const units = Array.from(unitMap.values())
  const components = Array.from(componentMap.values())
  const jobs = Array.from(jobMap.values())
  const inspections = Array.from(inspectionMap.values())
  const visualInspections = Array.from(visualInspectionMap.values())
  const visualInspectionForms = Array.from(visualInspectionFormMap.values())
  const visualInspectionSections = Array.from(
    visualInspectionSectionMap.values()
  )
  const visualInspectionFields = Array.from(visualInspectionFieldMap.values())
  const visualInspectionFormResponses = Array.from(
    visualInspectionFormResponseMap.values()
  )
  const visualInspectionSectionResponses = Array.from(
    visualInspectionSectionResponseMap.values()
  )
  const visualInspectionFieldResponses = Array.from(
    visualInspectionFieldResponseMap.values()
  )
  const reports = Array.from(reportMap.values())
  const componentDataFrames = Array.from(componentDataFrameMap.values())
  const componentDatasets = Array.from(componentDataSetMap.values())
  const actionLogs = Array.from(actionLogMap.values())
  const componentDefaults = Array.from(componentDefaultsMap.values())
  const markupLayers = Array.from(markupLayerMap.values())
  const assetModels = Array.from(assetModelMap.values())
  const photogrammetryModels = Array.from(photogrammetryModelMap.values())
  const kestrelModels = Array.from(kestrelModelMap.values())
  const thirdPartyInspectionDetails = Array.from(
    thirdPartyInspectionDetailsMap.values()
  )
  const potreePointClouds = Array.from(potreePointCloudMap.values())
  const pointCloudScenes = Array.from(pointCloudSceneMap.values())
  const siteObservations = Array.from(siteObservationMap.values())
  const observationTickets = Array.from(observationTicketMap.values())
  const siteModels = Array.from(siteModelMap.values())
  const geographicPins = Array.from(geographicPinMap.values())
  const userComments = Array.from(userCommentMap.values())
  const millPlans = Array.from(millPlanMap.values())
  const walkdowns = Array.from(walkdownMap.values())
  // There should always be a type of this entry, since we create a default
  // one if Rolodex doesn't return anything for it
  const duckDbFeatureFlag = entryClassObjs.find(
    (entry) => entry.type === 'forecast-duckdb-feature-flag'
  ) as DuckDbFeatureFlag

  return {
    // Entry Arrays
    // Don't return the duckdb feature flag
    entries: entries.filter((e) => e.type !== DUCKDB_FEATURE_FLAG_TYPE),
    organizations,
    sites,
    siteConfigs,
    units,
    components,
    jobs,
    inspections,
    reports,
    componentDataFrames,
    componentDatasets,
    actionLogs,
    markupLayers,
    assetModels,
    componentDefaults,
    photogrammetryModels: photogrammetryModels,
    kestrelModels,
    duckDbFeatureFlag,
    thirdPartyInspectionDetails,
    potreePointClouds,
    pointCloudScenes,
    siteObservations,
    observationTickets,
    siteModels,
    geographicPins,
    visualInspections,
    visualInspectionForms,
    visualInspectionSections,
    visualInspectionFields,
    visualInspectionFormResponses,
    visualInspectionSectionResponses,
    visualInspectionFieldResponses,
    userComments,
    millPlans,
    walkdowns,
    // Map accessors
    getEntry: (id) => allEntriesMap.get(id),
    getOrganization: (id) => organizationMap.get(id),
    getSite: (id) => siteMap.get(id),
    getSiteConfig: (id) => siteConfigMap.get(id),
    getUnit: (id) => unitMap.get(id),
    getComponent: (id) => componentMap.get(id),
    getJob: (id) => jobMap.get(id),
    getInspection: (id) => inspectionMap.get(id),
    getReport: (id) => reportMap.get(id),
    getComponentDataFrame: (id) => componentDataFrameMap.get(id),
    getComponentDataSet: (id) => componentDataSetMap.get(id),
    getActionLog: (id) => actionLogMap.get(id),
    getMarkupLayer: (id) => markupLayerMap.get(id),
    getAssetModel: (id) => assetModelMap.get(id),
    getPhotogrammetryModel: (id) => photogrammetryModelMap.get(id),
    getKestrelModel: (id) => kestrelModelMap.get(id),
    getSiteObservation: (id) => siteObservationMap.get(id),
    getObservationTicket: (id) => observationTicketMap.get(id),
    getSiteModel: (id) => siteModelMap.get(id),
    getGeographicPin: (id) => geographicPinMap.get(id),
    getVisualInspection: (id) => visualInspectionMap.get(id),
    getVisualInspectionForm: (id) => visualInspectionFormMap.get(id),
    getVisualInspectionSection: (id) => visualInspectionSectionMap.get(id),
    getVisualInspectionField: (id) => visualInspectionFieldMap.get(id),
    getVisualInspectionFormResponse: (id) =>
      visualInspectionFormResponseMap.get(id),
    getVisualInspectionSectionResponse: (id) =>
      visualInspectionSectionResponseMap.get(id),
    getVisualInspectionFieldResponse: (id) =>
      visualInspectionFieldResponseMap.get(id),
    getUserComment: (id) => userCommentMap.get(id),
    getMillPlan: (id) => millPlanMap.get(id),
    getWalkdown: (id) => walkdownMap.get(id),
  }
}

/**
 *
 * @param entry Rolodex Entry Json (presumably an asset model)
 * @returns the entry typed to the specific type of asset model (vertical tank, piping, etc....)
 * TODO: when we add more types of asset model expand this out to a switch
 */
function getAssetModelType(entry: RolodexEntryJson) {
  if (entry.data.type === GeckoAssetModelType.VerticalTank) {
    return entry as GeckoVerticalTankAssetModel
  } else if (entry.data.type === GeckoAssetModelType.LegacyBoiler) {
    return entry as GeckoLegacyBoilerAssetModel
  } else if (entry.data.type === GeckoAssetModelType.Piping) {
    return entry as GeckoPipelineAssetModel
  } else if (entry.data.type === GeckoAssetModelType.MachinedForging) {
    return entry as GeckoMachinedForgingAssetModel
  } else {
    return entry as GeckoAssetModel
  }
}

function createComponentDataFramesFromComponentDataset(
  cds: GeckoComponentDataset
): GeckoComponentDataFrame[] {
  const dfSettingsToUse = cds.data.metadata_flags.use_conduit
    ? cds.data.data_frame_settings
    : cds.data.ds_data_frame_settings

  const filteredDfSettings = Object.entries(dfSettingsToUse ?? {}).reduce<
    Record<string, DataFrameSettings>
  >((result, [columnName, dfSettings]) => {
    if (
      !dfSettings.ignore_cdf &&
      dfSettings.source.toString() !== GeckoSource.Nominal.id &&
      dfSettings.view.toString() !== GeckoView.DifferenceFromNominal.id
    ) {
      result[columnName] = dfSettings
    }
    return result
  }, {})
  return Object.entries(filteredDfSettings ?? {}).map(([columnName]) => {
    const cdfId = `${cds.id}-${columnName}`
    const cdfTags = {
      ...cds.tags,
      ['component-dataset']: cds.id,
    }

    return GeckoComponentDataFrame.CreateFromJSON({
      id: cdfId,
      type: RolodexEntryType.COMPONENT_DATA_FRAME,
      data: cds.generateCdfJson(columnName, dfSettingsToUse),
      tags: cdfTags,
      index: {},
      generation: cds.generation,
      updated_at: new Date(Date.now()).toISOString(),
      view_restriction: null,
    })
  })
}

function determineDuckDbFeatureFlag(
  loadedEntries: Array<Array<RolodexLoadedEntryJson>> | undefined
): DuckDbFeatureFlag {
  const loadedDuckDbEntries = loadedEntries?.find((arr) =>
    arr.find((loaded) => loaded.entry.type === DUCKDB_FEATURE_FLAG_TYPE)
  )
  if (loadedDuckDbEntries && loadedDuckDbEntries.length === 1) {
    return DuckDbFeatureFlag.CreateFromJSON(loadedDuckDbEntries[0].entry)
  }

  return DuckDbFeatureFlag.CreateDefault()
}
